import type { ClaimCandidate } from "@hp/types";
import { Card, CardBody, CardHeader, CardTitle, DataTable, StatusPill } from "@hp/ui";
import type { DigestPeriod } from "../../api/types";
import { formatDateTime, formatForum, formatINRRange, formatIssueTag } from "../../lib/format";

export interface DigestReportProps {
  period: DigestPeriod;
  previousPeriod?: DigestPeriod;
}

function midpoint(period: DigestPeriod): number {
  return (period.combined_value_low + period.combined_value_high) / 2;
}

export function DigestReport({ period, previousPeriod }: DigestReportProps) {
  const delta = previousPeriod ? midpoint(period) - midpoint(previousPeriod) : null;
  const deltaPct =
    previousPeriod && midpoint(previousPeriod) !== 0 ? (delta! / midpoint(previousPeriod)) * 100 : null;

  return (
    <Card>
      <CardHeader className="flex-wrap gap-2">
        <div>
          <CardTitle className="text-base">Claim Pipeline Digest &mdash; {period.label}</CardTitle>
          <p className="mt-0.5 text-xs text-slate-500">Auto-generated {formatDateTime(period.generated_at)}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide text-slate-500">
          {period.is_current ? "Current period (to date)" : "Closed period"}
        </span>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">New claim candidates</div>
            <div className="mt-1 font-mono text-4xl font-semibold tabular-nums text-slate-900">{period.new_count}</div>
            <div className="mt-1 text-sm text-slate-600">
              combined value{" "}
              <span className="font-mono font-medium tabular-nums text-slate-900">
                {formatINRRange(period.combined_value_low, period.combined_value_high)}
              </span>
            </div>
          </div>
          {previousPeriod && deltaPct !== null ? (
            <div className="text-right">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                vs {previousPeriod.label.split(" (")[0]}
              </div>
              <div
                className={[
                  "mt-1 font-mono text-lg font-semibold tabular-nums",
                  deltaPct >= 0 ? "text-emerald-700" : "text-slate-600",
                ].join(" ")}
              >
                {deltaPct >= 0 ? "▲" : "▼"} {Math.abs(deltaPct).toFixed(0)}%
              </div>
              <div className="text-xs text-slate-400">by combined mid-point value</div>
            </div>
          ) : null}
        </div>

        <p className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-700">{period.highlight}</p>

        {period.new_claim_candidates.length > 0 ? (
          <DataTable<ClaimCandidate>
            columns={[
              {
                key: "issue",
                header: "Issue",
                accessor: (row) => (
                  <div>
                    <div className="font-medium text-slate-900">{formatIssueTag(row.issue_tag)}</div>
                    <div className="text-xs text-slate-400">{formatForum(row.recommended_forum)}</div>
                  </div>
                ),
                sortValue: (row) => row.issue_tag,
              },
              {
                key: "value",
                header: "Estimated value",
                accessor: (row) => (
                  <span className="font-mono tabular-nums">{formatINRRange(row.value_low, row.value_high)}</span>
                ),
                sortValue: (row) => row.value_high,
              },
              {
                key: "confidence",
                header: "Confidence",
                accessor: (row) => (
                  <span className="font-mono tabular-nums">{Math.round(row.confidence_score * 100)}%</span>
                ),
                sortValue: (row) => row.confidence_score,
              },
              {
                key: "status",
                header: "Status now",
                accessor: (row) => <StatusPill kind="claim_candidate" status={row.status} />,
                sortValue: (row) => row.status,
              },
            ]}
            data={period.new_claim_candidates}
            rowKey={(row) => row.id}
            initialSort={{ key: "value", direction: "desc" }}
          />
        ) : (
          <p className="text-sm text-slate-400">No new claim candidates were identified this period.</p>
        )}
      </CardBody>
    </Card>
  );
}
